import { STORAGE_KEYS } from "./checkoutKeys";

const EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

interface StorageEntry<T> {
  value: T;
  expiresAt: number;
}

export function loadFromStorage<T>(userId: string, key: string): T | null {
  if (typeof window === "undefined") return null;

  try {
    const userKey = `${userId}_${key}`;
    const raw = localStorage.getItem(userKey);
    if (!raw) return null;

    const entry = JSON.parse(raw) as StorageEntry<T>;

    if (Date.now() > entry.expiresAt) {
      localStorage.removeItem(userKey);
      return null;
    }

    return entry.value;
  } catch {
    return null;
  }
}

export function saveToStorage<T>(userId: string, key: string, value: T) {
  if (typeof window === "undefined") return;

  const entry: StorageEntry<T> = {
    value,
    expiresAt: Date.now() + EXPIRY_MS,
  };

  const userKey = `${userId}_${key}`;
  localStorage.setItem(userKey, JSON.stringify(entry));
}

export function clearFromStorage(userId: string, key: string) {
  if (typeof window === "undefined") return;

  const userKey = `${userId}_${key}`;
  localStorage.removeItem(userKey);
}

export function clearOrdersFromStorage(userId: string) {
  clearFromStorage(userId, STORAGE_KEYS.ORDER_ADVANCED_SEARCH);
  clearFromStorage(userId, STORAGE_KEYS.ORDER_FILTERS);
  clearFromStorage(userId, STORAGE_KEYS.ORDER_PAGE_NUMBER);
  clearFromStorage(userId, STORAGE_KEYS.ORDER_PAGE_SIZE);
  clearFromStorage(userId, STORAGE_KEYS.ORDER_FILTERS_MONTHS);
}

export function clearCheckoutInfoFromStorage(userId: string) {
  clearFromStorage(userId, STORAGE_KEYS.BILLING);
  clearFromStorage(userId, STORAGE_KEYS.BILLING_ATTACHMENTS);
  clearFromStorage(userId, STORAGE_KEYS.PICKUP_DELIVERY);
  clearFromStorage(userId, STORAGE_KEYS.PAYMENT);
}

export async function saveFilesToStorage(
  userId: string,
  key: string,
  files: File[]
) {
  if (typeof window === "undefined") return;

  const encoded = await Promise.all(
    files.map(
      (file) =>
        new Promise<{ name: string; type: string; data: string }>(
          (resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () =>
              resolve({
                name: file.name,
                type: file.type,
                data: reader.result as string,
              });
            reader.onerror = reject;
            reader.readAsDataURL(file);
          }
        )
    )
  );

  const entry = {
    value: encoded,
    expiresAt: Date.now() + EXPIRY_MS,
  };

  const userKey = `${userId}_${key}`;
  localStorage.setItem(userKey, JSON.stringify(entry));
}

export function loadFilesFromStorage(
  userId: string,
  key: string
): File[] | null {
  try {
    if (typeof window === "undefined") return null;

    const userKey = `${userId}_${key}`;
    const raw = localStorage.getItem(userKey);
    if (!raw) return null;

    const entry = JSON.parse(raw) as {
      value: { name: string; type: string; data: string }[];
      expiresAt: number;
    };

    if (Date.now() > entry.expiresAt) {
      localStorage.removeItem(userKey);
      return null;
    }

    return entry.value.map((item) => {
      const base64 = item.data.split(",")[1];
      const byteString = atob(base64);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);

      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }

      return new File([ab], item.name, { type: item.type });
    });
  } catch {
    return null;
  }
}
